import { SxProps, Theme } from "@mui/material";
import { focusRing } from "theme/colors";
import emailConfigurationsTokens from "./EmailConfigurations.tokens";

const {
  palette,
  page,
  header,
  layout,
  section,
  badge,
  field,
  toggle,
  settingRow,
  nestedPanel,
  numberInput,
  optionRow,
  schedule,
  preview,
  esign,
} = emailConfigurationsTokens;

const focusVisibleRing = (offset: string) =>
  ({
    "&.Mui-focusVisible": {
      outline: `2px solid ${focusRing}`,
      outlineOffset: offset,
    },
  }) as const;

const emailConfigurationsStyles = {
  page: {
    maxWidth: page.maxWidth,
    mx: "auto",
    width: "100%",
    pt: { xs: "20px", md: page.paddingTop },
    px: { xs: "16px", md: page.paddingX },
    pb: { xs: "48px", md: page.paddingBottom },
    boxSizing: "border-box",
  } as SxProps<Theme>,

  title: {
    m: 0,
    mb: header.titleMarginBottom,
    fontSize: header.titleFontSize,
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    color: header.titleColor,
    lineHeight: 1.2,
  } as SxProps<Theme>,

  subtitle: {
    mb: header.subtitleMarginBottom,
    fontSize: header.subtitleFontSize,
    color: header.subtitleColor,
    lineHeight: 1.5,
  } as SxProps<Theme>,

  /** Accordions | sticky preview. Stacks below `lg`. */
  layout: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: `1fr ${layout.previewWidth}px`,
    },
    gap: layout.columnGap,
    alignItems: "start",
  } as SxProps<Theme>,

  sections: {
    display: "flex",
    flexDirection: "column",
    gap: layout.sectionGap,
    minWidth: 0,
  } as SxProps<Theme>,

  section: (open: boolean) =>
    ({
      border: `1px solid ${section.borderClosed}`,
      borderRadius: section.radius,
      bgcolor: section.bg,
      boxShadow: section.shadow,
    }) as SxProps<Theme>,

  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: section.headGap,
    width: "100%",
    p: section.headPadding,
    border: "none",
    borderRadius: section.radius,
    background: "none",
    textAlign: "left",
    textTransform: "none",
    cursor: "pointer",
    justifyContent: "flex-start",
    "&:hover": { background: "none" },
    ...focusVisibleRing("-2px"),
  } as SxProps<Theme>,

  sectionIcon: (open: boolean) =>
    ({
      width: section.iconTileSize,
      height: section.iconTileSize,
      borderRadius: section.iconTileRadius,
      bgcolor: section.iconTileBgClosed,
      color: section.iconColorClosed,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }) as SxProps<Theme>,

  sectionTitle: {
    fontSize: section.titleFontSize,
    fontWeight: section.titleFontWeight,
    color: section.titleColor,
    lineHeight: 1.3,
  } as SxProps<Theme>,

  sectionSub: {
    mt: section.subMarginTop,
    fontSize: section.subFontSize,
    color: section.subColor,
    lineHeight: 1.35,
  } as SxProps<Theme>,

  sectionBadge: {
    fontSize: badge.fontSize,
    fontWeight: badge.fontWeight,
    color: badge.color,
    bgcolor: badge.bg,
    border: `1px solid ${badge.border}`,
    borderRadius: badge.radius,
    p: badge.padding,
    whiteSpace: "nowrap",
    lineHeight: "normal",
    flexShrink: 0,
    // The badge lives inside a ButtonBase (a <button>). The app renders no
    // <CssBaseline/>, so buttons keep their UA font — `inherit` would pull
    // that (Arial) instead of Montserrat. Set it explicitly, like the
    // Typography siblings and the esign chips.
    fontFamily: "'Montserrat', sans-serif",
  } as SxProps<Theme>,

  sectionChevron: (open: boolean) =>
    ({
      width: section.chevronSize,
      height: section.chevronSize,
      color: section.chevronColor,
      flexShrink: 0,
      transform: open ? "rotate(180deg)" : "none",
    }) as SxProps<Theme>,

  sectionBody: (tight = false) =>
    ({
      p: tight ? section.bodyPaddingTight : section.bodyPadding,
      display: "flex",
      flexDirection: "column",
      ...(tight ? {} : { gap: section.bodyGap }),
    }) as SxProps<Theme>,

  field: {
    display: "flex",
    flexDirection: "column",
    gap: field.gap,
  } as SxProps<Theme>,

  fieldLabelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: field.gap,
  } as SxProps<Theme>,

  fieldLabel: {
    fontSize: field.labelFontSize,
    fontWeight: field.labelFontWeight,
    color: field.labelColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  fieldRequired: {
    color: field.requiredColor,
  } as SxProps<Theme>,

  fieldCounter: {
    fontSize: field.counterFontSize,
    color: field.counterColor,
    lineHeight: "normal",
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Subject: a boxed input that visually matches the message field below. */
  subjectInput: (error: boolean) =>
    ({
      width: "100%",
      height: field.inputHeight,
      border: `${field.inputBorderWidth} solid ${
        error ? field.errorColor : field.inputBorder
      }`,
      borderRadius: field.inputRadius,
      bgcolor: palette.white,
      boxSizing: "border-box",
      "&.Mui-focused": {
        borderColor: error ? field.errorColor : field.inputFocusBorder,
      },
      "& .MuiInputBase-input": {
        p: 0,
        px: field.inputPaddingX,
        height: "100%",
        boxSizing: "border-box",
        fontSize: field.inputFontSize,
        fontWeight: field.inputFontWeight,
        color: field.inputColor,
        lineHeight: "normal",
        "&::placeholder": {
          color: field.placeholderColor,
          opacity: 1,
          fontWeight: 500,
        },
      },
    }) as SxProps<Theme>,

  messageInput: {
    border: `${field.inputBorderWidth} solid ${field.inputBorder}`,
    borderRadius: field.areaRadius,
    p: field.areaPadding,
    "&.Mui-focused": { borderColor: field.inputFocusBorder },
    "& .MuiInputBase-input": {
      p: 0,
      fontSize: field.areaFontSize,
      color: field.inputColor,
      lineHeight: field.areaLineHeight,
      "&::placeholder": {
        color: field.placeholderColor,
        opacity: 1,
      },
    },
  } as SxProps<Theme>,

  toggle: (on: boolean) =>
    ({
      width: toggle.width,
      height: toggle.height,
      minWidth: toggle.width,
      p: 0,
      borderRadius: toggle.radius,
      bgcolor: on ? toggle.onBg : toggle.offBg,
      position: "relative",
      flexShrink: 0,
      "&:hover": { bgcolor: on ? toggle.onBg : toggle.offBg },
      "&.Mui-disabled": {
        bgcolor: on ? toggle.onBg : toggle.offBg,
        opacity: 0.5,
      },
      ...focusVisibleRing("2px"),
    }) as SxProps<Theme>,

  toggleKnob: (on: boolean) =>
    ({
      position: "absolute",
      top: `${toggle.knobOffset}px`,
      left: on ? `${toggle.knobOnLeft}px` : `${toggle.knobOffset}px`,
      width: toggle.knobSize,
      height: toggle.knobSize,
      borderRadius: "50%",
      bgcolor: toggle.knobBg,
      boxShadow: toggle.knobShadow,
      pointerEvents: "none",
    }) as SxProps<Theme>,

  settingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: settingRow.gap,
  } as SxProps<Theme>,

  settingLabel: (disabled = false) =>
    ({
      fontSize: settingRow.labelFontSize,
      fontWeight: settingRow.labelFontWeight,
      color: disabled ? palette.faint : settingRow.labelColor,
      lineHeight: 1.3,
    }) as SxProps<Theme>,

  settingDesc: {
    mt: settingRow.descMarginTop,
    fontSize: settingRow.descFontSize,
    color: settingRow.descColor,
    lineHeight: 1.35,
  } as SxProps<Theme>,

  divider: {
    height: "1px",
    bgcolor: settingRow.dividerColor,
  } as SxProps<Theme>,

  nestedPanel: {
    display: "flex",
    flexDirection: "column",
    gap: nestedPanel.gap,
    p: nestedPanel.padding,
    borderRadius: nestedPanel.radius,
    bgcolor: nestedPanel.bg,
    border: `1px solid ${nestedPanel.border}`,
  } as SxProps<Theme>,

  nestedRow: {
    display: "flex",
    alignItems: "center",
    gap: nestedPanel.rowGap,
  } as SxProps<Theme>,

  nestedLabel: (disabled = false) =>
    ({
      flex: 1,
      minWidth: 0,
      fontSize: nestedPanel.labelFontSize,
      color: disabled ? palette.faint : nestedPanel.labelColor,
      lineHeight: 1.4,
    }) as SxProps<Theme>,

  unit: {
    width: nestedPanel.unitWidth,
    flexShrink: 0,
    fontSize: nestedPanel.unitFontSize,
    color: nestedPanel.unitColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  numberInput: (wide = false, error = false) =>
    ({
      width: wide ? numberInput.wideWidth : numberInput.width,
      height: numberInput.height,
      flexShrink: 0,
      border: `${numberInput.borderWidth} solid ${
        error ? field.errorColor : numberInput.border
      }`,
      borderRadius: numberInput.radius,
      bgcolor: palette.white,
      boxSizing: "border-box",
      "&.Mui-focused": {
        borderColor: error ? field.errorColor : numberInput.focusBorder,
      },
      "&.Mui-disabled": { bgcolor: palette.white },
      "& .MuiInputBase-input": {
        p: 0,
        height: "100%",
        textAlign: "center",
        fontSize: numberInput.fontSize,
        fontWeight: numberInput.fontWeight,
        color: numberInput.color,
        lineHeight: "normal",
        "&.Mui-disabled": {
          WebkitTextFillColor: numberInput.disabledColor,
        },
      },
    }) as SxProps<Theme>,

  optionRow: (last: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: optionRow.gap,
      p: optionRow.padding,
      borderBottom: last ? "none" : `1px solid ${optionRow.divider}`,
    }) as SxProps<Theme>,

  scheduleBody: {
    display: "flex",
    flexDirection: "column",
    gap: schedule.gap,
  } as SxProps<Theme>,

  scheduleIntro: {
    fontSize: schedule.introFontSize,
    color: schedule.introColor,
    lineHeight: schedule.introLineHeight,
  } as SxProps<Theme>,

  scheduleCards: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: schedule.cardsGap,
  } as SxProps<Theme>,

  scheduleCard: (selected: boolean) =>
    ({
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: schedule.cardGap,
      minWidth: 0,
      p: schedule.cardPadding,
      border: `${schedule.cardBorderWidth} solid ${
        selected ? schedule.cardBorderOn : schedule.cardBorderOff
      }`,
      bgcolor: selected ? schedule.cardBgOn : schedule.cardBgOff,
      borderRadius: schedule.cardRadius,
      textAlign: "left",
      textTransform: "none",
      cursor: "pointer",
      "&:hover": {
        bgcolor: selected ? schedule.cardBgOn : schedule.cardBgOff,
        borderColor: selected ? schedule.cardBorderOn : schedule.cardBorderOff,
      },
      ...focusVisibleRing("2px"),
    }) as SxProps<Theme>,

  scheduleRadio: (selected: boolean) =>
    ({
      width: schedule.radioSize,
      height: schedule.radioSize,
      borderRadius: "50%",
      border: `1.5px solid ${
        selected ? schedule.radioBorderOn : schedule.radioBorderOff
      }`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      boxSizing: "border-box",
    }) as SxProps<Theme>,

  scheduleRadioDot: (selected: boolean) =>
    ({
      width: schedule.radioDotSize,
      height: schedule.radioDotSize,
      borderRadius: "50%",
      bgcolor: selected ? schedule.radioDotOn : "transparent",
    }) as SxProps<Theme>,

  scheduleCardLabel: {
    fontSize: schedule.labelFontSize,
    fontWeight: schedule.labelFontWeight,
    color: schedule.labelColor,
    lineHeight: 1.3,
  } as SxProps<Theme>,

  scheduleCardSub: {
    fontSize: schedule.subFontSize,
    color: schedule.subColor,
    lineHeight: 1.35,
  } as SxProps<Theme>,

  schedulePicked: {
    display: "flex",
    alignItems: "center",
    gap: schedule.pickedGap,
    p: schedule.pickedPadding,
    borderRadius: schedule.pickedRadius,
    bgcolor: schedule.pickedBg,
    border: `1px solid ${schedule.pickedBorder}`,
  } as SxProps<Theme>,

  schedulePickedField: {
    flex: 1,
    minWidth: 0,
    height: schedule.pickedFieldHeight,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    px: "10px",
    border: `1.5px solid ${numberInput.border}`,
    borderRadius: schedule.pickedFieldRadius,
    bgcolor: palette.white,
    boxSizing: "border-box",
    fontSize: schedule.pickedFieldFontSize,
    fontWeight: schedule.pickedFieldFontWeight,
    color: palette.ink,
    lineHeight: "normal",
    textTransform: "none",
    justifyContent: "space-between",
    "&:hover": { bgcolor: palette.white, borderColor: numberInput.focusBorder },
    ...focusVisibleRing("2px"),
  } as SxProps<Theme>,

  schedulePickedNote: {
    flexShrink: 0,
    fontSize: schedule.pickedNoteFontSize,
    color: schedule.pickedNoteColor,
    whiteSpace: "nowrap",
    lineHeight: "normal",
  } as SxProps<Theme>,

  /* ---------------------------------------------------------------- preview */

  previewSticky: {
    position: { xs: "static", lg: "sticky" },
    top: preview.stickyTop,
    minWidth: 0,
  } as SxProps<Theme>,

  previewCard: {
    border: `1px solid ${preview.border}`,
    borderRadius: preview.radius,
    overflow: "hidden",
    boxShadow: preview.shadow,
    bgcolor: preview.bg,
  } as SxProps<Theme>,

  previewHead: {
    display: "flex",
    alignItems: "center",
    gap: preview.headGap,
    p: preview.headPadding,
    bgcolor: preview.headBg,
    color: preview.headColor,
  } as SxProps<Theme>,

  previewHeadTitle: {
    fontSize: preview.headFontSize,
    fontWeight: preview.headFontWeight,
    lineHeight: "normal",
  } as SxProps<Theme>,

  previewLive: {
    ml: "auto",
    fontSize: preview.liveFontSize,
    fontWeight: preview.liveFontWeight,
    bgcolor: preview.liveBg,
    borderRadius: preview.liveRadius,
    p: preview.livePadding,
    lineHeight: "normal",
    flexShrink: 0,
  } as SxProps<Theme>,

  previewMeta: {
    p: preview.metaPadding,
    borderBottom: `1px solid ${preview.border}`,
    display: "flex",
    flexDirection: "column",
    gap: preview.metaGap,
  } as SxProps<Theme>,

  previewMetaRow: (truncate = false) =>
    ({
      fontSize: preview.metaFontSize,
      color: preview.metaColor,
      lineHeight: 1.5,
      ...(truncate
        ? {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }
        : { wordBreak: "break-word" }),
    }) as SxProps<Theme>,

  previewMetaLabel: {
    color: preview.metaLabelColor,
    fontWeight: 700,
  } as SxProps<Theme>,

  previewBody: {
    p: preview.bodyPadding,
    display: "flex",
    flexDirection: "column",
    gap: preview.bodyGap,
  } as SxProps<Theme>,

  previewGreeting: {
    fontSize: preview.greetFontSize,
    fontWeight: preview.greetFontWeight,
    color: preview.greetColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  previewPara: {
    fontSize: preview.paraFontSize,
    color: preview.paraColor,
    lineHeight: preview.paraLineHeight,
  } as SxProps<Theme>,

  previewParaStrong: {
    color: preview.metaLabelColor,
    fontWeight: 700,
  } as SxProps<Theme>,

  previewCta: (bg: string, color: string) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      alignSelf: "flex-start",
      height: preview.ctaHeight,
      minWidth: 0,
      px: preview.ctaPaddingX,
      borderRadius: preview.ctaRadius,
      bgcolor: bg,
      color,
      fontSize: preview.ctaFontSize,
      fontWeight: preview.ctaFontWeight,
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
    }) as SxProps<Theme>,

  previewNote: {
    fontSize: preview.noteFontSize,
    color: preview.noteColor,
    lineHeight: preview.noteLineHeight,
  } as SxProps<Theme>,

  previewSignOff: {
    fontSize: preview.signOffFontSize,
    color: preview.paraColor,
    lineHeight: preview.signOffLineHeight,
  } as SxProps<Theme>,

  previewTeam: (color: string) =>
    ({
      color,
      fontWeight: preview.teamFontWeight,
    }) as SxProps<Theme>,

  previewFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: preview.footerGap,
    flexWrap: "wrap",
    p: preview.footerPadding,
    borderTop: `1px solid ${preview.border}`,
    bgcolor: preview.footerBg,
  } as SxProps<Theme>,

  previewBrand: {
    display: "flex",
    alignItems: "center",
    gap: preview.footerGap,
  } as SxProps<Theme>,

  previewBrandBadge: (bg: string) =>
    ({
      bgcolor: bg,
      color: preview.headColor,
      fontSize: preview.brandFontSize,
      fontWeight: preview.brandFontWeight,
      borderRadius: preview.brandRadius,
      p: preview.brandPadding,
      lineHeight: "normal",
    }) as SxProps<Theme>,

  previewEsign: {
    fontSize: preview.esignFontSize,
    fontWeight: preview.esignFontWeight,
    color: preview.esignColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  previewCopyright: {
    fontSize: preview.copyrightFontSize,
    color: preview.copyrightColor,
    lineHeight: "normal",
    textAlign: "right",
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  } as SxProps<Theme>,

  previewLogo: {
    maxHeight: preview.logoMaxHeight,
    maxWidth: preview.logoMaxWidth,
    objectFit: "contain",
  } as SxProps<Theme>,

  /* ------------------------------------------------------------ eSign mode */

  esignRadio: {
    m: 0,
    "& .MuiRadio-root": {
      p: esign.radioPadding,
      "&:hover": { backgroundColor: "transparent" },
      ...focusVisibleRing("-2px"),
    },
  } as SxProps<Theme>,

  esignRemaining: {
    flexShrink: 0,
    width: esign.remainingWidth,
    textAlign: "right",
    fontSize: nestedPanel.unitFontSize,
    color: nestedPanel.unitColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  esignChip: (high: boolean) =>
    ({
      height: esign.chipHeight,
      bgcolor: high ? esign.highChipBg : esign.basicChipBg,
      color: high ? esign.highChipColor : esign.basicChipColor,
      fontFamily: "inherit",
      fontSize: esign.chipFontSize,
      fontWeight: esign.chipFontWeight,
    }) as SxProps<Theme>,
};

export default emailConfigurationsStyles;
